import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation, useNavigationType, Navigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, Wrench, Receipt, History as HistoryIcon, LogOut, Menu,
  Search, Bell, Boxes, Package, Palette, Recycle, Wallet, Database, Landmark, Coins, HandCoins, BookOpen,
  FileSpreadsheet, Activity, MapPin, ChevronDown, ChevronLeft, Building2, CalendarClock, HardHat, ClipboardCheck, Gauge, FileBarChart, Factory,
  CalendarDays, Users, UserCog, ShieldCheck, Sparkles, FileText, ShieldAlert, MonitorPlay, Scale, Plus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { privFor } from "@/lib/priv";
import { SHOW_PRODUCTION_MENU } from "@/config";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { confirmLeave } from "@/lib/navState";
import { MASTER_RESOURCES } from "@/lib/masterConfig";

const MGMT = ["Owner", "Admin", "GM", "Branch Manager", "Controller"];
const FIN = ["Finance Manager", "Admin Finance", "Controller"];
const OPS = ["Service Advisor", "Production", "QC", "Kepala Bengkel"];

const NAV_GROUPS = [
  { group: "Menu", items: [
    { to: "/menu", label: "Smart Menu", icon: LayoutDashboard },
  ]},
  { group: "War Room", items: [
    { to: "/war-room", label: "War Room", icon: MonitorPlay },
  ]},
  { group: "KPI & Karyawan", items: [
    { to: "/kpi-tim", label: "Dashboard KPI Tim", icon: Gauge, roles: [...MGMT, "Kepala Bengkel", "Kepala Produksi", "Foreman", "Production", "Business Manager"] },
    { to: "/karyawan", label: "Data Karyawan", icon: Users, roles: [...MGMT, "Kepala Bengkel", "Kepala Produksi", "Foreman", "HR GA"] },
    { to: "/pekerjaan-harian", label: "Pekerjaan Harian", icon: ClipboardCheck, roles: [...MGMT, "Kepala Bengkel", "Kepala Produksi", "Foreman", "Production"] },
    { to: "/riwayat-pekerjaan", label: "Riwayat Pekerjaan", icon: HistoryIcon, roles: [...MGMT, "Kepala Bengkel", "Kepala Produksi", "Foreman"] },
  ]},
  { group: "Jadwal", items: [
    { to: "/kalender", label: "Kalender", icon: CalendarDays, roles: [...MGMT, ...OPS, "Manager", "Kepala Produksi", "Foreman", "Technician", "Partman", "Customer Service"] },
  ]},
  { group: "Production", items: [
    { to: "/live-production", label: "Live Production", icon: Activity, roles: [...MGMT, ...OPS, "Kepala Produksi"] },
    { to: "/production-schedule", label: "Production Schedule", icon: CalendarClock, roles: [...MGMT, ...OPS, "Kepala Produksi"] },
    { to: "/tracking", label: "Unit Tracking", icon: MapPin, roles: [...MGMT, ...OPS, "Kepala Produksi", "Customer Service"] },
    { to: "/my-jobs", label: "My Jobs", icon: HardHat, roles: [...MGMT, ...OPS, "Kepala Produksi", "Technician"] },
    { to: "/production-floor", label: "Production Floor", icon: ClipboardCheck, roles: [...MGMT, "QC", "Kepala Bengkel", "Kepala Produksi", "Foreman"] },
    { to: "/kpi", label: "KPI & Performance", icon: Gauge, roles: [...MGMT, ...OPS, "Kepala Produksi", "Technician", "Business Manager"] },
    { to: "/reports", label: "Production Reports", icon: FileBarChart, roles: [...MGMT, "Kepala Produksi", "Finance", "Service Advisor", "Business Manager"] },
  ]},
  { group: "Claim", items: [
    { to: "/estimates", label: "Estimasi", icon: FileSpreadsheet, roles: [...MGMT, "Service Advisor", "Customer Service", "SA_CS", "ESTIMATOR_PARTMAN", "Estimator", "Kepala Bengkel"] },
    { to: "/pre-order", label: "Pre Order", icon: ClipboardList, roles: [...MGMT, "Service Advisor", "Customer Service"] },
    { to: "/repair-order", label: "Repair Order", icon: Wrench, roles: [...MGMT, ...OPS] },
    { to: "/finish-order", label: "Finish Order", icon: Receipt, roles: [...MGMT, "Service Advisor", "Finance", "Customer Service"] },
    { to: "/claims", label: "All Claims", icon: Search, end: true, roles: [...MGMT, "Service Advisor", "Finance", "Production", "QC", "Customer Service"] },
  ]},
  { group: "Sparepart", items: [
    { to: "/po-partman", label: "Partman", icon: ClipboardList, roles: [...MGMT, "Partman", "Warehouse"] },
    { to: "/parts-monitoring", label: "Waiting Parts", icon: Gauge, roles: [...MGMT, "Kepala Bengkel", "Partman", "Warehouse", "Service Advisor"] },
  ]},
  { group: "Warehouse", items: [
    { to: "/warehouse/parts", label: "Sparepart", icon: Boxes, roles: [...MGMT, "Warehouse", "Partman"] },
    { to: "/warehouse/material", label: "Material", icon: Package, roles: [...MGMT, "Warehouse", "Partman"] },
    { to: "/warehouse/paint", label: "Paint", icon: Palette, roles: [...MGMT, "Warehouse", "Partman"] },
    { to: "/warehouse/salvage", label: "Salvage", icon: Recycle, roles: [...MGMT, "Warehouse", "Partman"] },
  ]},
  { group: "Owner", ownerOnly: true, items: [
    { to: "/", label: "Owner Dashboard", icon: LayoutDashboard, end: true },
    { to: "/dashboard-harian", label: "Dashboard Harian", icon: CalendarClock },
    { to: "/owner/security", label: "Owner Security Center", icon: ShieldCheck, rootOnly: true },
    { to: "/owner/ai", label: "Owner Command AI", icon: Sparkles, rootOnly: true },
  ]},
  { group: "Portal Security", securityOnly: true, items: [
    { to: "/security/dashboard", label: "Dashboard Security", icon: LayoutDashboard, roles: ["Security"] },
    { to: "/security/pos", label: "Pos Security", icon: ShieldAlert, roles: ["Security"] },
  ]},
  { group: "Security Management", ownerOnly: true, items: [
    { to: "/security/log", label: "Log Kendaraan", icon: ClipboardList },
    { to: "/security/recon", label: "Rekonsiliasi", icon: Gauge },
    { to: "/security/reports", label: "Laporan Security", icon: FileText },
    { to: "/security/pos", label: "Pos Security (Monitoring)", icon: ShieldAlert },
  ]},
  { group: "Finance", items: [
    { to: "/finance-dashboard", label: "Dashboard Finance", opsLabel: "Dashboard Operasional", icon: Gauge, fin: [...FIN, "Business Manager"] },
    { to: "/fin/kas-bank", label: "Kas & Bank", icon: Landmark, fin: FIN },
    { to: "/fin/piutang-utang", label: "Piutang & Utang", icon: Wallet, fin: [...FIN, "Business Manager"] },
    { to: "/fin/laporan-kontrol", label: "Laporan & Kontrol", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
  ]},
  { group: "Finance", hidden: true, items: [
    { to: "/report-control", label: "Report & Control", icon: ClipboardList, fin: ["Finance Manager", "Controller"] },
    { to: "/or-payments", label: "Approval OR", icon: HandCoins, fin: FIN },
    { to: "/finance", label: "Receivable", icon: Wallet, fin: FIN },
    { to: "/recon-dashboard", label: "Rekonsiliasi Asuransi", icon: Scale, fin: [...FIN, "Business Manager"] },
    { to: "/accounts-payable", label: "Accounts Payable", icon: HandCoins, fin: FIN },
    { to: "/cash-bank", label: "Cash & Bank", icon: Landmark, fin: FIN },
    { to: "/petty-cash", label: "Petty Cash", icon: Coins, fin: FIN },
    { to: "/daybook", label: "Buku Kas Harian", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
    { to: "/accounting", label: "Jurnal & Laporan Keuangan", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
    { to: "/fin/kas-keluar", label: "Kas Keluar (Pengajuan)", icon: HandCoins, fin: FIN },
    { to: "/fin/approval-pembayaran", label: "Approval Pembayaran", icon: HandCoins, fin: FIN },
    { to: "/fin/closing", label: "Closing Bulanan", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
    { to: "/fin/audit-log", label: "Audit Log", icon: ShieldCheck, fin: ["Finance Manager", "Controller"] },
    { to: "/fin/coa", label: "Chart of Accounts", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
    { to: "/fin/cash-flow", label: "Cash Flow", icon: BookOpen, fin: ["Finance Manager", "Controller"] },
  ]},
  { group: "System", items: [
    { to: "/history", label: "History", icon: HistoryIcon, roles: [...MGMT, "Service Advisor", "Finance"] },
    { to: "/master-data", label: "Master Data", icon: Database, roles: MGMT },
    { to: "/master-data/privileges", label: "Privilege Pengguna", icon: ShieldCheck, ownerOnly: true },
  ]},
];

// Primary items for mobile bottom navigation (role-filtered, first 4 shown + Menu).
const BOTTOM_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: MGMT },
  { to: "/my-jobs", label: "Jobs", icon: HardHat, roles: [...MGMT, ...OPS, "Technician"] },
  { to: "/tracking", label: "Unit", icon: MapPin, roles: [...MGMT, ...OPS, "Customer Service"] },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, roles: [...MGMT, ...OPS, "Manager", "Kepala Produksi", "Foreman", "Technician", "Partman", "Customer Service"] },
  { to: "/warehouse/parts", label: "Parts", icon: Boxes, roles: [...MGMT, "Warehouse", "Partman"] },
  { to: "/claims", label: "Klaim", icon: Search, roles: [...MGMT, "Service Advisor", "Finance", "Production", "QC", "Customer Service"] },
];

function roleName(user) {
  return user?.role_name || (user?.role === "admin" ? "Administrator" : "Staff");
}

const ROLE_ALIASES = {
  SA_CS: ["Service Advisor", "Customer Service"],
  ESTIMATOR_PARTMAN: ["Partman", "Estimator"],
  "Finance Manager": ["Finance"],
  "Admin Finance": ["Finance"],
  "Kepala Produksi": ["Production"],
  HEAD_PRODUCTION: ["Kepala Produksi", "Production"],
  Foreman: ["Production"],
  "Business Manager": ["Manager"],
  KBG2: ["Kepala Bengkel"],
  Purchasing: ["Partman"],
};
function effRoleSet(user) {
  const rn = user?.role_name;
  const s = new Set(rn ? [rn] : []);
  const stack = [...s];
  while (stack.length) { const r = stack.pop(); (ROLE_ALIASES[r] || []).forEach((a) => { if (!s.has(a)) { s.add(a); stack.push(a); } }); }
  return s;
}
export function canSeeFor(user) {
  const isRoot = user?.role === "root";
  const isMgmt = user?.role === "admin" || isRoot || MGMT.includes(user?.role_name);
  const eff = effRoleSet(user);
  const isOwner = isRoot || user?.role_name === "Owner";
  return (n) => {
    if (n.ownerOnly) return isOwner;
    const pv = privFor(user, n.to);
    if (pv === "DENY") return false;
    if (pv === "ALLOW") return true;
    if (n.rootOnly) return isRoot;
    if (n.fin) return isOwner || n.fin.some((r) => eff.has(r));
    return !n.roles || isMgmt || n.roles.some((r) => eff.has(r));
  };
}

export function groupedNav(user) {
  const canSee = canSeeFor(user);
  const isOwnerRoot = user?.role === "root" || user?.role_name === "Owner" || user?.role_name === "ROOT_OWNER";
  const isSecurity = user?.role_name === "Security" && user?.role !== "admin" && user?.role !== "root";
  const isFinOps = user?.role_name === "Admin Finance";
  return NAV_GROUPS
    .filter((g) => SHOW_PRODUCTION_MENU || g.group !== "Production")
    .filter((g) => !(g.ownerOnly && !isOwnerRoot))
    .filter((g) => !(g.securityOnly && !isSecurity))
    .filter((g) => !g.hidden)
    .map((g) => ({ group: g.group, items: g.items.filter(canSee).map((i) => (i.opsLabel && isFinOps ? { ...i, label: i.opsLabel } : i)) })).filter((g) => g.items.length > 0);
}

function NavItems({ user, onClick, accordion = false }) {
  const groups = groupedNav(user);
  const storageKey = `ti_nav_group_${user?.user_id || user?.id || "anon"}`;

  // Desktop (multi-open): every group expanded, individually collapsible.
  const [multi, setMulti] = useState(() => Object.fromEntries(groups.map((g) => [g.group, true])));

  // Mobile (single-open accordion): default all collapsed, remember last-used group.
  const [single, setSingle] = useState(() => {
    if (!accordion) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && groups.some((g) => g.group === saved)) return saved;
    } catch (e) { /* ignore */ }
    return groups.length === 1 ? groups[0].group : null;
  });

  const isOpen = (g) => (accordion ? single === g : multi[g]);
  const toggle = (g) => {
    if (accordion) setSingle((cur) => (cur === g ? null : g));
    else setMulti((o) => ({ ...o, [g]: !o[g] }));
  };
  const handleItemClick = (g) => {
    if (accordion) { try { localStorage.setItem(storageKey, g); } catch (e) { /* ignore */ } }
    if (onClick) onClick();
  };

  return (
    <nav className="flex flex-col gap-0.5 px-3 pb-4">
      {groups.map((g) => {
        const groupOpen = isOpen(g.group);
        return (
        <div key={g.group} className="pt-3">
          <button
            data-testid={`navgroup-${g.group.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => toggle(g.group)}
            className="flex w-full items-center justify-between rounded px-3 pb-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-[#475569] transition-colors hover:text-[#111827]"
          >
            {g.group}
            <ChevronDown size={13} className={`transition-transform ${groupOpen ? "" : "-rotate-90"}`} />
          </button>
          {groupOpen && g.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => handleItemClick(g.group)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `group relative flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#20466D] ${
                  isActive ? "bg-[#FBF3DF] font-bold text-[#111827] ring-1 ring-inset ring-[#B88929]" : "text-[#1F2937] hover:bg-[#F4F5F7] hover:text-[#111827]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`absolute left-0 top-1/2 h-5 -translate-y-1/2 rounded-r-full transition-all ${isActive ? "w-1 ti-gold-bg" : "w-0"}`} />
                  <n.icon size={19} strokeWidth={2.1} className={isActive ? "text-[#8A641A]" : "text-[#475569] group-hover:text-[#111827]"} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      );})}
    </nav>
  );
}

function Brand() {
  return (
    <div className="border-b border-gray-100 px-5 py-5">
      <img src="/titanium-logo-light.png" alt="TITANIUM" data-testid="brand-logo" className="h-20 w-auto max-w-full object-contain" />
      <div className="mt-2 text-sm font-extrabold tracking-[0.28em] text-[#111827]">TITANIUM <span className="text-[#8A641A]">i.OS</span></div>
    </div>
  );
}

function BranchSelector() {
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/master/location").then((r) => {
      const docs = r.data || [];
      const list = docs.map((d) => d.name || d.location_name || d.code).filter(Boolean);
      setBranches(list);
      const def = docs.find((d) => /puspowarno/i.test(d.location_name || "")) || docs.find((d) => /titanium/i.test(d.business_unit || "") && !/karoseri/i.test(d.business_unit || ""));
      if (list.length) setSel(def ? def.location_name : list[0]);
    }).catch(() => {});
  }, []);
  const label = sel || "Titanium HQ";
  return (
    <div className="relative">
      <button data-testid="branch-selector" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:border-[#D4AF37]">
        <Building2 size={15} className="text-[#9A7209]" />
        <span className="max-w-[220px] truncate">{label}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {open && branches.length > 0 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            {branches.map((b) => (
              <button key={b} data-testid={`branch-opt-${b}`} onClick={() => { setSel(b); setOpen(false); }} className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 ${b === sel ? "font-semibold text-blue-600" : "text-slate-700"}`}>
                <Building2 size={14} /> {b}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const go = () => { navigate(`/claims${q.trim() ? `?search=${encodeURIComponent(q.trim())}` : ""}`); };
  return (
    <div className="relative w-full max-w-md">
      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") go(); }}
        placeholder="Cari plat nomor, customer, atau nomor SPK…"
        className="h-10 w-full rounded-full border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm text-[#111827] placeholder:text-gray-400 outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20"
      />
    </div>
  );
}

function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data.notifications || []); setUnread(data.unread_count || 0); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const openItem = async (n) => {
    setOpen(false);
    try { await api.patch(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    load(); navigate(`/claims/${n.claim_id}`);
  };
  const markAll = async () => { try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ } load(); };
  return (
    <div className="relative">
      <button data-testid="notif-bell-btn" onClick={() => setOpen((o) => { const nxt = !o; if (nxt) load(); return nxt; })} className="relative rounded-lg p-2 transition-colors hover:bg-gray-100">
        <Bell size={19} className="text-gray-600" />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel" className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
              <span className="font-heading text-sm font-semibold text-slate-900">Notifikasi</span>
              {unread > 0 && <button data-testid="notif-readall-btn" onClick={markAll} className="text-xs font-medium ti-gold hover:underline">Tandai dibaca</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">Belum ada notifikasi</p>}
              {items.map((n) => (
                <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)} className={`flex w-full flex-col items-start gap-0.5 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/60"}`}>
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="h-2 w-2 rounded-full bg-red-500" />}
                    <span className="ti-plate text-xs">{n.license_plate}</span>
                  </div>
                  <span className="text-sm font-semibold text-emerald-600">{n.message}</span>
                  <span className="text-xs text-slate-500">{n.customer_name || "—"}{n.spk_number ? ` · SPK ${n.spk_number}` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BottomNav({ user, onMenu }) {
  const canSee = canSeeFor(user);
  const items = BOTTOM_NAV.filter(canSee).slice(0, 4);
  return (
    <nav data-testid="mobile-bottom-nav" className="fixed inset-x-0 bottom-0 z-50 flex h-16 items-stretch justify-around border-t border-gray-200 bg-white/95 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.12)] backdrop-blur-xl lg:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={`bottomnav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `group relative flex flex-1 flex-col items-center justify-center gap-1 transition-colors ${isActive ? "text-[#8A641A]" : "text-[#475569] hover:text-[#111827]"}`
          }
        >
          {({ isActive }) => (
            <>
              <span className={`absolute top-0 h-1 w-10 rounded-full transition-all ${isActive ? "bg-[#B88929]" : "bg-transparent"}`} />
              <n.icon size={22} strokeWidth={2.1} />
              <span className="text-[12px] font-bold tracking-wide">{n.label}</span>
            </>
          )}
        </NavLink>
      ))}
      <button
        data-testid="bottomnav-menu"
        onClick={onMenu}
        className="group flex flex-1 flex-col items-center justify-center gap-1 text-[#475569] transition-colors hover:text-[#111827]"
      >
        <Menu size={22} strokeWidth={2.1} />
        <span className="text-[12px] font-bold tracking-wide">Menu</span>
      </button>
    </nav>
  );
}

const NAV_LABELS = Object.fromEntries(NAV_GROUPS.flatMap((g) => g.items.map((i) => [i.to, { label: i.label, group: g.group }])));
const EXTRA_LABELS = { "/claims": "Claims", "/profile": "Profil", "/master-data": "Master Data", "/tracking": "Tracking", "/kalender": "Kalender", "/workshop": "Workshop", "/billing": "Billing", "/history": "History" };
const labelOf = (p) => NAV_LABELS[p]?.label || EXTRA_LABELS[p];

function crumbsFor(pathname, ops) {
  const segs = pathname.split("/").filter(Boolean);
  const out = [{ to: "/", label: "Dashboard" }];
  const grp = NAV_LABELS[pathname]?.group;
  if (grp && !labelOf("/" + segs[0])) out.push({ label: grp });
  let acc = "";
  segs.forEach((s, i) => {
    acc += "/" + s;
    const l = ops && acc === "/finance-dashboard" ? "Dashboard Operasional" : labelOf(acc);
    if (l) out.push({ to: acc, label: l });
    else if (i === segs.length - 1 || !NAV_LABELS[pathname]) {
      if (i > 0) out.push({ to: acc, label: /^[a-f0-9]{24}$/.test(s) ? "Detail" : decodeURIComponent(s).toUpperCase() });
      else out.push({ label: decodeURIComponent(s).replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) });
    }
  });
  return out;
}

function parentOf(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  while (segs.length > 1) { segs.pop(); const p = "/" + segs.join("/"); if (labelOf(p)) return p; }
  return "/";
}

function useScrollMemory() {
  const loc = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    const key = `ti-scroll:${loc.key}`;
    if (navType === "POP") {
      const y = Number(sessionStorage.getItem(key) || 0);
      const t = setTimeout(() => window.scrollTo(0, y), 350);
      return () => { clearTimeout(t); sessionStorage.setItem(key, String(window.scrollY)); };
    }
    window.scrollTo(0, 0);
    return () => { sessionStorage.setItem(key, String(window.scrollY)); };
  }, [loc.key, navType]);
}

function BackBar() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { user: bbUser } = useAuth();
  useScrollMemory();
  if (privFor(bbUser, loc.pathname) === "DENY") return <Navigate to="/" replace />;
  if (loc.pathname === "/") return null;
  const crumbs = crumbsFor(loc.pathname, bbUser?.role_name === "Admin Finance");
  const md = new URLSearchParams(loc.search).get("md");
  if (md && MASTER_RESOURCES[md]) crumbs.push({ label: MASTER_RESOURCES[md].label });
  else if (md === "__vehicles__") crumbs.push({ label: "Vehicle Master" });
  const back = () => {
    if (!confirmLeave()) return;
    window.__tiDirty = false;
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(loc.search ? loc.pathname : parentOf(loc.pathname), { replace: true });
  };
  const go = (to) => { if (confirmLeave()) { window.__tiDirty = false; navigate(to); } };
  return (
    <div className="mb-3 flex min-w-0 items-center gap-2" data-testid="back-bar">
      <button onClick={back} data-testid="back-btn" aria-label="Kembali"
        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-blue-400 hover:text-blue-600">
        <ChevronLeft size={16} /> <span className="hidden sm:inline">Kembali</span>
      </button>
      <nav data-testid="breadcrumb" className="flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap text-xs text-slate-500">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-slate-300">/</span>}
            {c.to && i < crumbs.length - 1
              ? <button data-testid={`crumb-${i}`} onClick={() => go(c.to)} className="hover:text-blue-600">{c.label}</button>
              : <span data-testid={`crumb-${i}`} className={i === crumbs.length - 1 ? "font-semibold text-slate-800" : ""}>{c.label}</span>}
          </span>
        ))}
      </nav>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-gray-200 ti-side lg:flex">
        <Brand />
        <div className="min-h-0 flex-1 overflow-y-auto pb-4"><NavItems user={user} /></div>
        <div className="border-t ti-hair p-3">
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ti-gold-bg text-xs font-bold text-white">{initials}</div>
            <button data-testid="profile-link" onClick={() => navigate("/profile")} className="min-w-0 flex-1 text-left">
              <div className="truncate text-sm font-semibold text-[#111827] hover:text-[#9A7209]">{user?.name}</div>
              <div className="truncate text-xs font-semibold ti-gold">{roleName(user)}</div>
            </button>
            <button data-testid="logout-btn" onClick={doLogout} title="Logout" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-[#111827]">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Shared mobile menu drawer (opened from bottom-nav "Menu") */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="flex h-[100dvh] w-[82vw] max-w-[320px] flex-col border-none ti-side p-0">
          <SheetTitle className="sr-only">Menu navigasi</SheetTitle>
          <div className="shrink-0"><Brand /></div>
          <div data-testid="mobile-nav-scroll" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            <NavItems user={user} onClick={() => setOpen(false)} accordion />
          </div>
          <div className="shrink-0 border-t border-gray-200 p-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
            <button data-testid="logout-btn-mobile" onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-[#111827]">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile header (compact) */}
        <header className="flex items-center justify-between border-b border-gray-200 ti-topbar px-4 py-2 lg:hidden">
          <div className="flex items-center gap-2"><img src="/titanium-logo-light.png" alt="TITANIUM" className="h-11 w-auto object-contain" /><span className="text-sm font-extrabold tracking-[0.16em] text-[#8A641A]">i.OS</span></div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button data-testid="mobile-profile-btn" onClick={() => navigate("/profile")} className="flex h-8 w-8 items-center justify-center rounded-lg ti-gold-bg text-[11px] font-bold text-white">{initials}</button>
          </div>
        </header>

        {/* Desktop top bar */}
        <header className="hidden items-center gap-4 border-b border-gray-200 ti-topbar px-6 py-2 lg:flex">
          <BranchSelector />
          <div className="flex flex-1 justify-center"><GlobalSearch /></div>
          <div className="flex items-center gap-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs">
              <span className="text-[#475569]">Role:</span> <span className="font-bold text-[#8A641A]">{roleName(user)}</span>
            </div>
            <NotificationBell />
          </div>
        </header>

        <main className="ti-canvas min-w-0 flex-1 bg-white p-3 pb-24 sm:p-5 lg:pb-5">
          <BackBar />
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <BottomNav user={user} onMenu={() => navigate("/menu")} />
    </div>
  );
}
